import type { FormikProps } from "formik";
import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGlobalState } from "senf-shared";
import type { Project } from "senf-shared/types";
import { getDemographicItems, getYears } from "../../../util/demographicUtils";
import Geocoder from "../../atoms/geocoder/Geocoder";
import { Combobox } from "../../core/inputs/Combobox";
import { Input } from "../../core/inputs/Input";

interface DemographicItem {
  name: string;
  type: string;
  label: string;
  options?: Array<{ value: string; label: string }>;
  disabled?: boolean;
}

interface DemographicsFormProps {
  formik?: FormikProps<any>;
  project: Project;
}

const DemographicsForm: FC<DemographicsFormProps> = ({ formik, project }) => {
  const { t } = useTranslation();
  const years = useMemo(() => getYears(), []);
  const [translation] = useGlobalState().translationsState;
  const containerRef = useRef<HTMLDivElement>(null);
  const [isNarrow, setIsNarrow] = useState(false);
  const [statefulMap, setStatefulMap] = useGlobalState().mapState;
  const [gender, setGender] = useState<string | null>(null);

  useEffect(() => {
    const checkWidth = () => {
      if (containerRef.current) {
        setIsNarrow(containerRef.current.offsetWidth < 500);
      }
    };

    checkWidth();
    window.addEventListener("resize", checkWidth);
    return () => window.removeEventListener("resize", checkWidth);
  }, []);

  const demographicItems = useMemo(
    () => getDemographicItems(project),
    [project],
  );

  const renderField = (item: DemographicItem) => {
    if (item.disabled) return null;

    const commonProps = formik
      ? {
          onChange: ({ value }) => {
            console.log("value", value);
            formik.setFieldValue(item.name, value);
          },
          onBlur: formik.handleBlur,
          value: formik.values[item.name] || "",
          error: formik.touched[item.name] && Boolean(formik.errors[item.name]),
          note: formik.touched[item.name]
            ? formik.errors[item.name]?.toString()
            : undefined,
          size: "xl" as const,
        }
      : {
          size: "xl" as const,
          value: undefined,
        };

    switch (item.name) {
      case "postcode":
        return (
          // <Input
          //   name={item.name}
          //   required={!project?.settings?.demographicData?.withoutPostcode}
          //   type="text"
          //   label={t("global:postcode")}
          //   {...commonProps}
          // />
          project?.location?.outerBbox ? (
            <Geocoder
              statefulMap={statefulMap}
              geolocate={false}
              types="postcode"
              leadingIcon={false}
              label={t("global:postcode")}
              placeholder={" "}
              size="xl"
              value={formik?.values.postcode}
              setValue={(value) => {
                formik?.setFieldValue("postcode", value);
              }}
              callback={(item) => {
                formik?.setFieldValue("postcode", item.text);
              }}
              bbox={project?.location?.outerBbox}
              proximity={{
                longitude: project?.location.longitude,
                latitude: project?.location.latitude,
              }}
              shouldFly={false}
            />
          ) : (
            <Input
              name={item.name}
              required={!project?.settings?.demographicData?.withoutPostcode}
              type="text"
              placeholder={t("global:postcode")}
              {...commonProps}
              onChange={(e) => {
                console.log("e", e);
                formik?.setFieldValue("postcode", e.target.value);
              }}
            />
          )
        );
      case "gender":
        return (
          <>
            <Combobox
              {...commonProps}
              required={true}
              label={t("global:gender")}
              options={(item.options || []).map((option) => ({
                value: option.value,
                label: t(`global:${option.value}`),
              }))}
            />
          </>
        );
      case "age":
        return (
          <Combobox
            required={true}
            label={t("auth:authOptions.birthYear")}
            options={years}
            {...commonProps}
          />
        );
    }

    switch (item.type) {
      case "text":
        return (
          <Combobox
            options={years}
            label={translation?.[item.label] || item.label}
            {...commonProps}
          />
        );
      case "singlechoice":
        return (
          <Combobox
            label={translation?.[item.label] || item.label}
            options={(item.options || []).map((option) => ({
              value: option.value,
              label: translation?.[option.label] || option.label,
            }))}
            {...commonProps}
          />
        );
      default:
        return null;
    }
  };

  if (!demographicItems) {
    console.error("Demographic data fields are undefined");
    return null;
  }

  return (
    <div className="flex flex-col gap-4" ref={containerRef}>
      <div
        className={`grid ${demographicItems.filter((item) => !item.disabled).length > 3 ? (isNarrow ? "grid-cols-1" : "grid-cols-2") : "grid-cols-2"} gap-4`}
      >
        {demographicItems.map((item, index) => {
          const isFirstItem = index === 0;
          const enabledFields = demographicItems.filter(
            (field) => !field.disabled,
          );
          const isOddNumberOfFields = enabledFields.length % 2 !== 0;
          const isTwoFields = enabledFields.length === 2;
          const shouldSpanFullWidth =
            isTwoFields || (isFirstItem && isOddNumberOfFields);

          if (item.disabled) return null;

          return (
            <div
              key={item.name}
              className={`${
                shouldSpanFullWidth ? "col-span-full" : "col-span-1"
              }`}
            >
              {renderField(item)}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DemographicsForm;
